/**
 * OnboardingChecklist Component
 * Progress checklist showing setup tasks for new users
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Progress } from '@/components/ui/progress';
import { useOnboardingContext } from './OnboardingProvider';
import { onboardingChecklist } from '@/config/onboardingSteps';
import type { OnboardingChecklistItem } from '@/types/onboarding';

interface OnboardingChecklistProps {
  position?: 'floating' | 'sidebar';
  className?: string;
}

/**
 * OnboardingChecklist Component
 */
export const OnboardingChecklist: React.FC<OnboardingChecklistProps> = ({
  position = 'floating',
  className = '',
}) => {
  const {
    progress,
    completeChecklistItem,
    uncompleteChecklistItem,
    isChecklistItemCompleted,
    setOnboardingComplete,
  } = useOnboardingContext();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);

  /**
   * Calculate progress percentage
   */
  const completedCount = onboardingChecklist.filter((item) =>
    isChecklistItemCompleted(item.id)
  ).length;
  const totalCount = onboardingChecklist.filter((item) => !item.optional).length;
  const progressPercentage = Math.round((completedCount / totalCount) * 100);

  /**
   * Check if all required items are completed
   */
  const isAllComplete = onboardingChecklist
    .filter((item) => !item.optional)
    .every((item) => isChecklistItemCompleted(item.id));

  /**
   * Show confetti animation when all tasks are completed
   */
  useEffect(() => {
    if (isAllComplete && !progress.onboardingComplete) {
      setShowConfetti(true);
      const timer = setTimeout(() => {
        setShowConfetti(false);
        setOnboardingComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isAllComplete, progress.onboardingComplete, setOnboardingComplete]);

  /**
   * Handle dismiss
   */
  const handleDismiss = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  const checklistContent = (
    <div
      className={`bg-white dark:bg-espresso-800 rounded-lg shadow-lg border border-neutral-200 dark:border-espresso-700 overflow-hidden transition-all duration-300 ${
        position === 'floating'
          ? 'w-96 max-w-[calc(100vw-2rem)]'
          : 'w-full'
      } ${className}`}
    >
      {/* Header */}
      <div className="p-4 bg-gradient-to-r from-terracotta-50 to-cream-100 dark:from-terracotta-900/20 dark:to-espresso-700/30 border-b border-neutral-200 dark:border-espresso-700">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-espresso-900 dark:text-cream-100">
            Getting Started
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <button
              onClick={handleDismiss}
              className="p-1 text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 transition-colors"
              aria-label="Dismiss"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-neutral-600 dark:text-neutral-300">
              {completedCount} of {totalCount} completed
            </span>
            <span className="font-medium text-terracotta-700 dark:text-terracotta-400">
              {progressPercentage}%
            </span>
          </div>
          <Progress
            value={progressPercentage}
            className="h-2 bg-cream-200 dark:bg-espresso-600"
          />
        </div>
      </div>

      {/* Checklist items */}
      {isExpanded && (
        <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
          {onboardingChecklist.map((item) => (
            <ChecklistItem
              key={item.id}
              item={item}
              completed={isChecklistItemCompleted(item.id)}
              onToggle={(completed) => {
                if (completed) {
                  completeChecklistItem(item.id);
                } else {
                  uncompleteChecklistItem(item.id);
                }
              }}
            />
          ))}
        </div>
      )}

      {/* Celebration message */}
      {isAllComplete && showConfetti && (
        <div className="p-4 bg-success-50 dark:bg-success-900/20 border-t border-success-200 dark:border-success-800 animate-in slide-in-from-bottom duration-300">
          <div className="flex items-center gap-3">
            <div className="text-2xl">🎉</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-success-700 dark:text-success-400">
                Congratulations!
              </p>
              <p className="text-xs text-success-600 dark:text-success-500">
                You've completed the onboarding checklist.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render as floating or inline
  if (position === 'floating') {
    return createPortal(
      <div className="fixed bottom-6 right-6 z-[9000] animate-in slide-in-from-bottom-4 duration-500">
        {checklistContent}
      </div>,
      document.body
    );
  }

  return checklistContent;
};

/**
 * ChecklistItem Component
 */
interface ChecklistItemProps {
  item: OnboardingChecklistItem;
  completed: boolean;
  onToggle: (completed: boolean) => void;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({ item, completed, onToggle }) => {
  const handleClick = () => {
    if (item.action) {
      item.action.onClick();
    }
  };

  return (
    <div
      className={`group flex items-start gap-3 p-3 rounded-lg transition-all duration-200 ${
        completed
          ? 'bg-success-50 dark:bg-success-900/10 border border-success-200 dark:border-success-800'
          : 'bg-neutral-50 dark:bg-espresso-700/30 border border-transparent hover:border-neutral-200 dark:hover:border-espresso-600'
      }`}
    >
      {/* Checkbox */}
      <button
        onClick={() => onToggle(!completed)}
        className="flex-shrink-0 mt-0.5"
        aria-label={completed ? 'Mark as incomplete' : 'Mark as complete'}
      >
        <div
          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
            completed
              ? 'bg-success-600 border-success-600'
              : 'bg-white dark:bg-espresso-800 border-neutral-300 dark:border-espresso-600 group-hover:border-terracotta-400'
          }`}
        >
          {completed && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={3}
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h4
            className={`text-sm font-medium transition-colors ${
              completed
                ? 'text-neutral-500 dark:text-neutral-400 line-through'
                : 'text-espresso-900 dark:text-cream-100'
            }`}
          >
            {item.title}
            {item.optional && (
              <span className="ml-2 text-xs text-neutral-400 dark:text-neutral-500">
                (Optional)
              </span>
            )}
          </h4>
        </div>
        <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-1 leading-relaxed">
          {item.description}
        </p>

        {/* Action button */}
        {item.action && !completed && (
          <button
            onClick={handleClick}
            className="mt-2 text-xs font-medium text-terracotta-700 dark:text-terracotta-400 hover:text-terracotta-800 dark:hover:text-terracotta-300 transition-colors"
          >
            {item.action.label} →
          </button>
        )}
      </div>
    </div>
  );
};
